// HashAuditProject.jsx
import React, { useState, useRef, useEffect } from "react";
import "./HashAudit.css";

// ----------------------------
// Backend API functions
// ----------------------------
const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection";

// Verify key and fetch initial hashes for this account
async function verifyKey(key) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ method: "hash_audit", action: "verify_key", key }),
  });

  const response = await res.json();

  let data;
  try {
    data = typeof response.body === "string" ? JSON.parse(response.body) : response.body;
  } catch (e) {
    console.error("Failed to parse backend response:", response);
    return null;
  }

  if (!data?.verified) return null;

  // Normalize hashes: ensure each has `id` field
  return data.hashes.map(h => ({
    id: h.hash_id ?? h.id,
    hash: h.hash,
    plaintext: h.plaintext ?? "",
    correct_password: h.correct_password ?? "",
  }));
}

// Save a cracked password for a hash
async function savePassword(key, hash_id, plaintext) {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      method: "hash_audit",
      action: "save_password",
      key,
      hash_id,
      plaintext,
    }),
  });

  const response = await res.json();
  const data = JSON.parse(response.body);
  return data.ok === true;
}

// ----------------------------
// React Component
// ----------------------------
export default function HashAuditProject() {
  const [key, setKey] = useState("");
  const [verified, setVerified] = useState(false);

  const [toCrack, setToCrack] = useState([]);
  const [saved, setSaved] = useState([]);

  const [saving, setSaving] = useState({});
  const [terminal, setTerminal] = useState("Ready.\n");
  const terminalRef = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminal]);

  const log = msg =>
    setTerminal(t => t + `[${new Date().toLocaleTimeString()}] ${msg}\n`);

  // ----------------------------
  // Verify key
  // ----------------------------
  const handleVerify = async () => {
    log("Verifying key...");
    const hashes = await verifyKey(key.trim());

    if (hashes) {
      setVerified(true);

      const toCrackHashes = hashes.filter(h => !h.plaintext);
      const savedHashes = hashes.filter(h => h.plaintext);

      setToCrack(toCrackHashes);
      setSaved(savedHashes);

      log(`✅ Key verified. ${toCrackHashes.length} hashes to crack, ${savedHashes.length} saved.`);
    } else {
      log("❌ Invalid key. Access denied.");
    }
  };

  // ----------------------------
  // Handle input change
  // ----------------------------
  const handleChange = (id, value) => {
    setToCrack(prev =>
      prev.map(h => (h.id === id ? { ...h, plaintext: value } : h))
    );
  };

  // ----------------------------
  // Save cracked password
  // ----------------------------
  const handleSave = async id => {
    const item = toCrack.find(h => h.id === id);
    if (!item) return;

    setSaving(s => ({ ...s, [id]: true }));
    log(`Saving password for hash #${id}...`);

    const ok = await savePassword(key, id, item.plaintext);
    if (ok) {
      log(`✅ Password for hash #${id} saved.`);

      // Move to saved panel
      setSaved(prev => [...prev, item]);
      setToCrack(prev => prev.filter(h => h.id !== id));
    } else {
      log(`❌ Failed to save password for hash #${id}.`);
    }

    setSaving(s => ({ ...s, [id]: false }));
  };

  // ----------------------------
  // Download remaining hashes
  // ----------------------------
  const handleDownload = () => {
    const hashLines = toCrack.map(h => h.hash).join("\n");
    const blob = new Blob([hashLines], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hashes.txt";
    a.click();
    URL.revokeObjectURL(url);
    log("⬇️ Hash list downloaded as hashes.txt");
  };

  return (
    <div className="hashlab-container">
      <h1 className="hashlab-title">🔐 Password Audit & Remediation Project</h1>
      <p className="hashlab-sub">
        Enter your unique project key to access the hashes. After verification,
        you can record cracked passwords.
      </p>

      <div className="hashlab-grid">
        {/* ---------------- Left Panel ---------------- */}
        <div className="panel">
          <h2>Student Portal</h2>

          {!verified && (
            <>
              <label>Unique Key (provided by instructor)</label>
              <input
                type="text"
                placeholder="Enter your key"
                value={key}
                onChange={e => setKey(e.target.value)}
              />
              <div className="row">
                <button onClick={handleVerify} disabled={!key}>
                  Verify Key
                </button>
              </div>
            </>
          )}

          {verified && (
            <>
              <div className="row" style={{ marginBottom: "10px" }}>
                <button onClick={handleDownload}>Download Hashes (.txt)</button>
              </div>

              <div className="scroll-panel">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Hash</th>
                      <th>Your cracked password</th>
                      <th>Save</th>
                    </tr>
                  </thead>
                  <tbody>
                    {toCrack.map((h, i) => (
                      <tr key={h.id}>
                        <td>{i + 1}</td>
                        <td className="mono">{h.hash}</td>
                        <td>
                          <input
                            type="text"
                            value={h.plaintext}
                            placeholder="Enter plaintext"
                            onChange={e => handleChange(h.id, e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            onClick={() => handleSave(h.id)}
                            disabled={saving[h.id]}
                          >
                            {saving[h.id] ? "Saving..." : "Save"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* ---------------- Middle Panel ---------------- */}
        <div className="panel terminal-panel">
          <h2>Terminal</h2>
          <pre ref={terminalRef} className="terminal">{terminal}</pre>
        </div>

        {/* ---------------- Right Panel ---------------- */}
        {verified && (
          <div className="panel">
            <h2>Already Saved Passwords</h2>
            <div className="scroll-panel">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Hash</th>
                    <th>Cracked Password</th>
                  </tr>
                </thead>
                <tbody>
                  {saved.map((h, i) => (
                    <tr key={h.id}>
                      <td>{i + 1}</td>
                      <td className="mono">{h.hash}</td>
                      <td>{h.plaintext}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="footer-note">
        This portal records your cracked passwords for your pentest report.
      </div>
    </div>
  );
}
